import { useState, useEffect, useCallback } from "react";
import { Search, Download, Eye, Plus, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, API, TOKEN_KEY } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const STATUSES = ["pending", "confirmed", "completed", "cancelled", "rejected", "awaiting_payment"];
const PAYMENTS = ["unpaid", "paid", "pay_at_salon", "refunded"];
const STATUS_AR = { pending: "قيد المراجعة", confirmed: "مؤكد", completed: "مكتمل", cancelled: "ملغي", rejected: "مرفوض", awaiting_payment: "بانتظار الدفع" };
const PAY_AR = { unpaid: "غير مدفوع", paid: "مدفوع", pay_at_salon: "الدفع في الصالون", refunded: "مسترد" };
const COLORS = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700", completed: "bg-sky-100 text-sky-700", cancelled: "bg-rose-100 text-rose-700", rejected: "bg-rose-100 text-rose-700", awaiting_payment: "bg-orange-100 text-orange-700" };

export default function BookingsManage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    if (date) params.set("date", date);
    try {
      const { data } = await api.get(`/admin/bookings?${params}`);
      setRows(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [status, q, date]);

  useEffect(() => { load(); }, [status, date]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/bookings/${id}`, body);
      toast.success("تم التحديث");
      load();
      if (selected?.id === id) setSelected({ ...selected, ...body });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const notify = async (id) => {
    try { await api.post(`/admin/bookings/${id}/notify`); toast.success("تم إرسال البريد"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/admin/bookings/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "bookings.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">إدارة الحجوزات</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} className="rounded-full border-2" data-testid="export-csv-btn"><Download className="h-4 w-4 me-1" /> تصدير CSV</Button>
          <Button onClick={() => setShowManual(true)} className="rounded-full" data-testid="add-manual-booking-btn"><Plus className="h-4 w-4 me-1" /> حجز يدوي</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="بحث بالاسم أو الجوال أو الرقم المرجعي" className="rounded-xl h-11 ps-9" data-testid="bookings-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-xl h-11 w-40" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_AR[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl h-11 w-44" data-testid="bookings-date-filter" />
      </div>

      <div className="rounded-2xl bg-card cb-card-shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground border-b border-border">
            <tr>
              <th className="text-start p-3 font-medium">المرجع</th>
              <th className="text-start p-3 font-medium">الاسم</th>
              <th className="text-start p-3 font-medium">الجوال</th>
              <th className="text-start p-3 font-medium">الموعد</th>
              <th className="text-start p-3 font-medium">الحالة</th>
              <th className="text-start p-3 font-medium">الدفع</th>
              <th className="text-start p-3 font-medium">الإجمالي</th>
              <th className="text-start p-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">لا توجد حجوزات</td></tr>
              : rows.map((b) => (
                <tr key={b.id} className="border-b border-border/60 hover:bg-muted/40" data-testid={`booking-row-${b.id}`}>
                  <td className="p-3 font-mono text-xs" dir="ltr">{b.reference}</td>
                  <td className="p-3 font-medium">{b.customer.name}</td>
                  <td className="p-3" dir="ltr">{b.customer.phone}</td>
                  <td className="p-3">{b.date} · {b.time}</td>
                  <td className="p-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${COLORS[b.status]}`}>{STATUS_AR[b.status]}</span></td>
                  <td className="p-3 text-xs">{PAY_AR[b.payment_status] || b.payment_status}</td>
                  <td className="p-3 font-semibold">{b.total}</td>
                  <td className="p-3"><Button size="sm" variant="ghost" onClick={() => setSelected(b)} data-testid={`view-booking-${b.id}`}><Eye className="h-4 w-4" /></Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Details dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
          {selected && (
            <>
              <DialogHeader><DialogTitle>حجز {selected.reference}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">الاسم:</span> {selected.customer.name}</div>
                  <div><span className="text-muted-foreground">الجوال:</span> <span dir="ltr">{selected.customer.phone}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">البريد:</span> <span dir="ltr">{selected.customer.email}</span></div>
                  <div><span className="text-muted-foreground">التاريخ:</span> {selected.date}</div>
                  <div><span className="text-muted-foreground">الوقت:</span> <span dir="ltr">{selected.time}</span></div>
                </div>
                <div className="rounded-xl bg-muted/50 p-3">
                  {selected.services.map((s, i) => <div key={i} className="flex justify-between"><span>{s.name_ar}</span><span>{s.price} ر.س</span></div>)}
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-border"><span>الإجمالي</span><span>{selected.total} ر.س</span></div>
                </div>
                {selected.customer.notes && <div><span className="text-muted-foreground">ملاحظات العميلة:</span> {selected.customer.notes}</div>}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block text-xs">الحالة</Label>
                    <Select value={selected.status} onValueChange={(v) => patch(selected.id, { status: v })}>
                      <SelectTrigger className="rounded-xl h-10" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_AR[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">حالة الدفع</Label>
                    <Select value={selected.payment_status} onValueChange={(v) => patch(selected.id, { payment_status: v })}>
                      <SelectTrigger className="rounded-xl h-10" data-testid="edit-payment-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYMENTS.map((s) => <SelectItem key={s} value={s}>{PAY_AR[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">ملاحظات داخلية</Label>
                  <Textarea defaultValue={selected.internal_notes} onBlur={(e) => patch(selected.id, { internal_notes: e.target.value })} className="rounded-xl" rows={2} data-testid="internal-notes-input" />
                </div>
                {selected.payment_id && (
                  <div className="text-xs text-muted-foreground">
                    <div>رقم عملية الدفع: <span dir="ltr">{selected.payment_id}</span></div>
                    {selected.operation_id && <div>رقم العملية: <span dir="ltr">{selected.operation_id}</span></div>}
                  </div>
                )}
                <Button variant="outline" onClick={() => notify(selected.id)} className="rounded-full border-2 w-full" data-testid="notify-email-btn"><Mail className="h-4 w-4 me-1" /> إرسال تحديث بالبريد</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManualBookingDialog open={showManual} onClose={() => setShowManual(false)} onDone={load} />
    </div>
  );
}

function ManualBookingDialog({ open, onClose, onDone }) {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "10:00" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) api.get("/services?all=true").then((r) => setServices(r.data)); }, [open]);

  const save = async () => {
    if (!selected.length || !form.name || !form.phone || !form.date) { toast.error("أكملي الحقول المطلوبة"); return; }
    setSaving(true);
    try {
      await api.post("/admin/bookings", {
        services: selected, date: form.date, time: form.time,
        customer: { name: form.name, phone: form.phone, email: form.email || "guest@cloudbeauty.local" },
        agree_policy: true,
      });
      toast.success("تم إنشاء الحجز");
      onDone(); onClose();
      setSelected([]); setForm({ name: "", phone: "", email: "", date: "", time: "10:00" });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle>حجز يدوي جديد</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="الاسم" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11" data-testid="manual-name" />
          <Input placeholder="الجوال" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="manual-phone" />
          <Input placeholder="البريد (اختياري)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl h-11" dir="ltr" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl h-11" data-testid="manual-date" />
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-xl h-11" dir="ltr" data-testid="manual-time" />
          </div>
          <div className="max-h-44 overflow-y-auto space-y-1 border border-border rounded-xl p-2">
            {services.map((s) => (
              <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted cursor-pointer text-sm">
                <input type="checkbox" checked={selected.includes(s.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, s.id] : selected.filter((x) => x !== s.id))} />
                <span className="flex-1">{s.name_ar}</span><span className="text-primary font-semibold">{s.price}</span>
              </label>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full w-full h-11 font-semibold" data-testid="manual-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "إنشاء الحجز"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
